import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  requestDeleteSupplier,
  requestDeleteSuppliers,
  useSupplierById,
} from '../../..'
import { CustomConfirmDialog } from '../../../../../../../components/dialogs'
import { useToastContext } from '../../../../../../../contexts'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'

export const DeleteSuppliers: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)
  const { supplierIds } = useParams()

  const supplierIdsToDelete: string[] = supplierIds?.split(',') || []
  const supplierId =
    supplierIdsToDelete.length === 1 ? supplierIdsToDelete[0] : null

  const { data: supplier, isLoading } = useSupplierById(supplierId)

  const handleDelete = async () => {
    try {
      setIsRequestRunning(true)

      await submitDelete()

      handleClose()

      showToast({
        severity: 'success',
        summary: t(
          'masterDataManagement.suppliers.table.delete.dialog.delete.messages.success.summary',
        ),
        detail: t(
          'masterDataManagement.suppliers.table.delete.dialog.delete.messages.success.detail',
          {
            count: supplierIdsToDelete?.length,
            company: supplier?.Common.Company,
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'masterDataManagement.suppliers.table.delete.dialog.delete.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const submitDelete = async () => {
    if (supplierIdsToDelete.length === 1) {
      await requestDeleteSupplier(supplierIdsToDelete[0])
      return
    } else {
      await requestDeleteSuppliers(supplierIdsToDelete)
    }
  }

  const handleClose = () => {
    navigate('/master-data-management/suppliers')
  }

  return (
    <CustomConfirmDialog
      severity="danger"
      isVisible
      header={t('masterDataManagement.suppliers.table.delete.dialog.title', {
        count: supplierIdsToDelete?.length,
      })}
      message={t('masterDataManagement.suppliers.table.delete.dialog.message', {
        count: supplierIdsToDelete?.length,
        company: supplier?.Common.Company,
      })}
      onHide={handleClose}
      onReject={handleClose}
      rejectLabel={t(
        'masterDataManagement.suppliers.table.delete.dialog.cancel.label',
      )}
      onAccept={handleDelete}
      acceptLabel={t(
        'masterDataManagement.suppliers.table.delete.dialog.delete.label',
      )}
    />
  )
}
